// src/js/globals/SmoothScroll.js
//
// Lenis-driven smooth scroll. Two reasons we need this:
//   1. Stops the native rubber-band overscroll that exposes the gap between
//      our fixed WebGL canvas and the scrolling DOM at the page's start/end.
//   2. Smooths the scroll itself, which makes the WebGL bg + glass surfaces
//      stay perfectly aligned with the DOM as the user scrolls.
//
// Touch devices: we don't smooth — native momentum is good and Lenis's
// syncTouch can fight with mobile gestures. We only block overscroll via CSS.
//
// Behaviours reach this module via accessors (getLenis/scrollTo) rather than
// the eventBus — deliberate: SmoothScroll loads before behaviours hydrate, so
// boot order is deterministic and no subscription/replay machinery is needed.

import Lenis from 'lenis';

// Singleton access for behaviours (HeroStage, JourneyTimeline). Stays null on
// touch devices, where we keep native scroll and never instantiate Lenis.
let lenisInstance = null;
let scrollTriggerSynced = false;

// The ScrollTrigger plugin itself, handed to us by the first behaviour that
// imports it. Held so anything can ask for a re-measure without pulling gsap
// into its own bundle — see refreshScrollTriggers.
let scrollTriggerPlugin = null;

/**
 * Get the live Lenis instance.
 * Null on touch devices (native scroll) and before SmoothScroll boots.
 * @return {Lenis|null}
 */
/**
 * Read the reduced-motion preference at call time, not at module load.
 * A reader can change it mid-session (macOS and Windows both apply it
 * live), and `scrollTo` may be called minutes after boot.
 * @return {boolean}
 */
function prefersReducedMotion() {
	return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function getLenis() {
	return lenisInstance;
}

/**
 * Scroll to a target — Lenis-smoothed when available, native otherwise.
 * @param {number|string|HTMLElement} target
 * @param {object} [options] Lenis scrollTo options — duration etc. are
 *   Lenis-only; the native fallback ignores them.
 */
export function scrollTo(target, options = {}) {
	if (lenisInstance) {
		lenisInstance.scrollTo(target, options);
		return;
	}

	// `behavior: 'smooth'` would animate the viewport — the one thing the
	// reduced-motion path exists to avoid. Note this branch is REACHED under
	// reduced motion precisely because Lenis was never created, so it cannot
	// simply inherit the guard above.
	window.scrollTo({
		top: target,
		behavior: prefersReducedMotion() ? 'auto' : 'smooth',
	});
}

/**
 * Sync GSAP ScrollTrigger's update to Lenis's smoothed scroll position.
 * Behaviours dynamically import gsap/ScrollTrigger, then hand ScrollTrigger to
 * us here. Idempotent — multiple behaviours can call it without stacking
 * listeners. No-op on touch (native scroll already drives ScrollTrigger).
 * Keeps gsap out of this bundle.
 * @param {object} ScrollTrigger The ScrollTrigger plugin, already registered
 */
export function registerScrollTriggerSync(ScrollTrigger) {
	// Captured BEFORE the Lenis guard on purpose. The scroll sync below is
	// desktop-only, but refreshScrollTriggers() is not: a touch device runs
	// the same pinned timelines off native scroll and mis-measures them in
	// exactly the same way, so it needs the reference too.
	scrollTriggerPlugin = ScrollTrigger;

	if (!lenisInstance || scrollTriggerSynced) return;

	lenisInstance.on('scroll', ScrollTrigger.update);
	scrollTriggerSynced = true;
}

/**
 * Re-measure every ScrollTrigger on the page.
 *
 * For content that changes the height of the DOCUMENT after boot. ScrollTrigger
 * auto-refreshes on `visibilitychange,DOMContentLoaded,load,resize` only, and a
 * page that GROWS in place fires none of them — every trigger below the growth
 * keeps the start/end it measured at boot, so a pin engages at a stale position
 * and jumps to catch up. ConsultAgent is the current caller: its answer expands
 * the hero, which pushes [data-journey] down past its measured start.
 *
 * No-op until a behaviour has handed us the plugin via
 * registerScrollTriggerSync — same shape as the guards above, and it keeps gsap
 * out of this bundle and out of every caller's.
 * @return {void}
 */
export function refreshScrollTriggers() {
	if (!scrollTriggerPlugin) return;

	scrollTriggerPlugin.refresh();
}

function SmoothScroll() {
	const isTouch = window.matchMedia('(pointer: coarse)').matches;

	// Smoothing scroll IS animating it. A reader who has asked their OS for
	// reduced motion has asked for exactly this not to happen, and smoothed
	// scrolling is one of the more common vestibular triggers — every wheel
	// tick becomes a ~1s eased animation of the whole viewport. Bail before
	// instantiating Lenis rather than instantiating and disabling it, so no
	// rAF loop runs and `getLenis()` reports null: the same shape as the
	// touch path, which every consumer already handles.
	//
	// The native `scroll-behavior: smooth` on <html> has to go too, and that
	// is CSS — see the matching media query in foundations/foundation.css.
	// Without it this bail-out just hands the animation back to the browser.
	if (isTouch || prefersReducedMotion()) {
		// Native scroll — just block the bounce
		document.documentElement.style.overscrollBehavior = 'none';
		document.body.style.overscrollBehavior = 'none';
		return () => {};
	}

	const lenis = new Lenis({
		duration: 1.0,
		// classic ease-out — fast attack, gentle settle
		easing: t => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
		smoothWheel: true,
		wheelMultiplier: 1,
		syncTouch: false,
		gestureOrientation: 'vertical',
	});

	lenisInstance = lenis;

	// Standard RAF loop. Lenis dispatches native scroll events, so existing
	// scroll-listening code (GSAP ScrollTrigger, IntersectionObserver, etc.)
	// keeps working without modification — registerScrollTriggerSync exists
	// only to move ScrollTrigger's update into the same tick as Lenis's
	// smoothed position, so scrubbed animations never lag a frame behind.
	let rafId = null;
	function raf(time) {
		lenis.raf(time);
		rafId = requestAnimationFrame(raf);
	}
	rafId = requestAnimationFrame(raf);

	return () => {
		if (rafId) cancelAnimationFrame(rafId);
		lenis.destroy();
		lenisInstance = null;
		scrollTriggerSynced = false;
		// scrollTriggerPlugin deliberately survives. It is the plugin module,
		// not per-instance state, and the behaviours that own the triggers
		// have their own lifecycle. Nulling it would open a window where a
		// live trigger exists but refreshScrollTriggers() silently no-ops;
		// keeping it only risks a refresh() against zero triggers, which
		// ScrollTrigger already handles.
	};
}

export default SmoothScroll;
